import { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Star, MessageSquare, Video, Upload, CheckCircle, AlertCircle, Clock, ThumbsUp, TrendingUp, Share2, Send, XCircle } from 'lucide-react';

type TestimonialType = 'written' | 'video';
type TestimonialStatus = 'pending' | 'approved' | 'featured' | 'rejected';
type AttributionLevel = 'full' | 'first_name' | 'anonymous';
type ResultType = 'revenue_growth' | 'customer_growth' | 'time_saved' | 'efficiency' | 'skill_development' | 'network_expansion';
type TriggerPoint = 'module_completion' | 'revenue_milestone' | 'referral_success' | 'subscription_renewal' | 'support_ticket';

interface TestimonialData {
  customerName: string;
  businessName: string;
  industry: string;
  duration: string;
  tier: string;
  testimonialType: TestimonialType;
  starRating: number;
  testimonialText: string;
  biggestWin: string;
  revenueGrowth: number;
  customerGrowth: number;
  whatSurprised: string;
  whatTellOthers: string;
  results: ResultType[];
  permissionToPublish: boolean;
  attributionLevel: AttributionLevel;
  socialHandle: string;
  status: TestimonialStatus;
}

const INDUSTRIES = [
  'Accounting & Finance', 'Architecture & Design', 'Automotive', 'Beauty & Wellness',
  'Construction & Trades', 'Consulting & Coaching', 'Education & Training',
  'Entertainment & Media', 'Food & Beverage', 'Health & Medical', 'IT & Technology',
  'Legal Services', 'Marketing & Advertising', 'Photography & Videography',
  'Real Estate', 'Retail & E-commerce', 'Sports & Fitness', 'Tourism & Hospitality',
  'Transportation & Logistics', 'Other',
];

const DURATIONS = ['Less than 1 month', '1-3 months', '3-6 months', '6-12 months', 'More than 12 months'];

const TIERS = ['FREE (1-Pager Website)', 'Goal-to-100 (R197/mo)', 'Goal-to-1K (R497/mo)', 'Goal-to-10K (R997/mo)'];

const TRIGGER_POINTS: { key: TriggerPoint; label: string; condition: string; icon: typeof TrendingUp }[] = [
  { key: 'module_completion', label: 'Module Completion', condition: 'After completing 3+ modules', icon: CheckCircle },
  { key: 'revenue_milestone', label: 'Revenue Milestone', condition: 'After 50% revenue growth', icon: TrendingUp },
  { key: 'referral_success', label: 'Referral Success', condition: 'After 5+ referrals', icon: Share2 },
  { key: 'subscription_renewal', label: 'Subscription Renewal', condition: 'At 3, 6, 12 month marks', icon: Clock },
  { key: 'support_ticket', label: 'Support Ticket Closed', condition: 'After positive resolution', icon: ThumbsUp },
];

const RESULT_OPTIONS: { key: ResultType; label: string }[] = [
  { key: 'revenue_growth', label: 'Revenue growth' },
  { key: 'customer_growth', label: 'Customer growth' },
  { key: 'time_saved', label: 'Time saved' },
  { key: 'efficiency', label: 'Efficiency gains' },
  { key: 'skill_development', label: 'Skill development' },
  { key: 'network_expansion', label: 'Network expansion' },
];

const STATUS_CONFIG: Record<TestimonialStatus, { label: string; icon: typeof Clock; color: string; bg: string }> = {
  pending: { label: 'Pending', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
  approved: { label: 'Approved', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  featured: { label: 'Featured', icon: Star, color: 'text-blue-600', bg: 'bg-blue-50' },
  rejected: { label: 'Rejected', icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
};

const POPIA_NOTICE = "By submitting this testimonial, you consent to Chrysalinks Group using your name, business name, and testimonial text for marketing purposes. You can withdraw consent at any time by emailing support@chrysalinks.co.za.";

const THANK_YOU_MESSAGE = `Thank you for sharing your success story!

Your testimonial means the world to us and will help other entrepreneurs see what's possible.

What happens next:
1. Our team reviews your testimonial
2. We may reach out for a quick interview
3. With your permission, we'll share your story

If you have any questions, just reply to this email.

Thank you for being part of the Chrysalinks family!

Best regards,
The Chrysalinks Team`;

type ViewMode = 'submit' | 'admin';

export default function TestimonialForm() {
  const [mode, setMode] = useState<ViewMode>('submit');
  const [data, setData] = useState<TestimonialData>({
    customerName: '', businessName: '', industry: '', duration: '', tier: '',
    testimonialType: 'written', starRating: 5, testimonialText: '',
    biggestWin: '', revenueGrowth: 0, customerGrowth: 0, whatSurprised: '',
    whatTellOthers: '', results: [], permissionToPublish: false,
    attributionLevel: 'full', socialHandle: '', status: 'pending',
  });
  const [triggerPoint, setTriggerPoint] = useState<TriggerPoint>('module_completion');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [adminFilter, setAdminFilter] = useState<TestimonialStatus | 'all'>('all');

  const updateField = useCallback(<K extends keyof TestimonialData>(key: K, value: TestimonialData[K]) => {
    setData(prev => ({ ...prev, [key]: value }));
    setError(null);
  }, []);

  const toggleResult = useCallback((result: ResultType) => {
    setData(prev => ({
      ...prev,
      results: prev.results.includes(result)
        ? prev.results.filter(r => r !== result)
        : [...prev.results, result],
    }));
  }, []);

  const handlePhotoUpload = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (ev) => setSelectedPhoto(ev.target?.result as string);
        reader.readAsDataURL(file);
      }
    };
    input.click();
  }, []);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const missing: string[] = [];
      if (!data.customerName) missing.push('Your Name');
      if (!data.businessName) missing.push('Business Name');
      if (!data.industry) missing.push('Industry');
      if (!data.duration) missing.push('Duration');
      if (!data.tier) missing.push('Tier');
      if (!data.biggestWin) missing.push('Biggest win');
      if (!data.whatSurprised) missing.push('What surprised you');
      if (!data.whatTellOthers) missing.push('What would you tell others');
      if (data.testimonialType === 'written' && (!data.testimonialText || data.testimonialText.length < 20)) missing.push('Testimonial text (min 20 characters)');
      if (!data.permissionToPublish) missing.push('Permission to publish');

      if (missing.length > 0) {
        setError(`Please fill in: ${missing.join(', ')}`);
        setLoading(false);
        return;
      }

      await new Promise(r => setTimeout(r, 800));
      setSubmitted(true);
    } catch (err: any) {
      setError('Submission failed: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [data]);

  const handleAdminAction = useCallback((action: TestimonialStatus) => {
    setData(prev => ({ ...prev, status: action }));
  }, []);

  const resetForm = useCallback(() => {
    setData({
      customerName: '', businessName: '', industry: '', duration: '', tier: '',
      testimonialType: 'written', starRating: 5, testimonialText: '',
      biggestWin: '', revenueGrowth: 0, customerGrowth: 0, whatSurprised: '',
      whatTellOthers: '', results: [], permissionToPublish: false,
      attributionLevel: 'full', socialHandle: '', status: 'pending',
    });
    setSubmitted(false);
    setSelectedPhoto(null);
    setError(null);
  }, []);

  const StatusBadge = STATUS_CONFIG[data.status];

  const adminQueue = useMemo(() => {
    const items: Array<{ name: string; business: string; submitted: string; status: TestimonialStatus; rating: number }> = [
      { name: data.customerName || 'Waiting...', business: data.businessName, submitted: new Date().toLocaleDateString(), status: data.status, rating: data.starRating },
    ];
    if (adminFilter !== 'all') return items.filter(i => i.status === adminFilter);
    return items;
  }, [data, adminFilter]);

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-[2rem] border border-slate-200 p-8 lg:p-10 text-center space-y-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Thank You!</h2>
          <div className="bg-slate-50 rounded-xl p-6 text-left">
            <p className="text-xs text-slate-700 whitespace-pre-line leading-relaxed">{THANK_YOU_MESSAGE}</p>
          </div>
          <button onClick={resetForm}
            className="px-6 py-3 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 transition-colors">
            Submit Another
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex gap-2 mb-2">
        <button onClick={() => setMode('submit')}
          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
            mode === 'submit' ? 'bg-amber-100 text-amber-700' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
          }`}>
          <Send size={14} className="inline mr-1" />
          Submit Testimonial
        </button>
        <button onClick={() => setMode('admin')}
          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
            mode === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
          }`}>
          <Star size={14} className="inline mr-1" />
          Admin Queue
        </button>
      </div>

      {mode === 'admin' ? (
        <div className="bg-white rounded-[2rem] border border-slate-200 p-6 lg:p-8 space-y-5">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Admin Queue</h2>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Review & approve testimonials</p>
            </div>
            <div className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${StatusBadge.bg} ${StatusBadge.color}`}>
              {StatusBadge.label}
            </div>
          </div>

          <div className="flex gap-2 overflow-x-auto">
            {(['all', 'pending', 'approved', 'featured', 'rejected'] as const).map(f => (
              <button key={f} onClick={() => setAdminFilter(f)}
                className={`shrink-0 px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
                  adminFilter === f ? 'bg-amber-100 text-amber-700' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                }`}>
                {f === 'all' ? 'All' : STATUS_CONFIG[f].label}
              </button>
            ))}
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          {adminQueue.length === 0 ? (
            <div className="text-center py-10 text-slate-400">
              <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-xs font-bold">No testimonials match this filter</p>
            </div>
          ) : (
            <div className="space-y-3">
              {adminQueue.map((item, idx) => (
                <div key={idx} className="border border-slate-200 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-800">{item.name}</p>
                      <p className="text-xs text-slate-500">{item.business} — Submitted {item.submitted}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} size={12} className={i < item.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'} />
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleAdminAction('approved')}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-green-700 transition-colors">
                      Approve
                    </button>
                    <button onClick={() => handleAdminAction('featured')}
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 transition-colors">
                      Feature
                    </button>
                    <button onClick={() => handleAdminAction('rejected')}
                      className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-[10px] font-black uppercase tracking-widest hover:bg-red-700 transition-colors">
                      Reject
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-4 pt-4 border-t border-slate-100">
            <div className="text-center">
              <p className="text-lg font-black text-slate-800">--</p>
              <p className="text-[8px] font-black uppercase tracking-widest text-slate-500">Submission Rate</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-black text-slate-800">--</p>
              <p className="text-[8px] font-black uppercase tracking-widest text-slate-500">Approval Rate</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-black text-slate-800">--</p>
              <p className="text-[8px] font-black uppercase tracking-widest text-slate-500">Featured Count</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-[2rem] border border-slate-200 p-6 lg:p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="bg-amber-100 p-3 rounded-2xl text-amber-700">
              <MessageSquare size={24} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-800 uppercase tracking-tight">Share Your Success</h2>
              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5">Your story inspires others</p>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
              <AlertCircle size={14} />
              {error}
            </div>
          )}

          <div>
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-700 mb-3">What Triggered This?</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {TRIGGER_POINTS.map(tp => (
                <button key={tp.key} onClick={() => setTriggerPoint(tp.key)}
                  className={`p-3 rounded-xl border text-left transition-all ${
                    triggerPoint === tp.key
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                  }`}>
                  <tp.icon size={16} className={triggerPoint === tp.key ? 'text-amber-600' : 'text-slate-400'} />
                  <p className="text-[10px] font-black text-slate-700 mt-1 uppercase tracking-tight">{tp.label}</p>
                  <p className="text-[8px] text-slate-500 mt-0.5">{tp.condition}</p>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Your Name *</label>
              <input type="text" value={data.customerName} onChange={e => updateField('customerName', e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Business Name *</label>
              <input type="text" value={data.businessName} onChange={e => updateField('businessName', e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Industry *</label>
              <select value={data.industry} onChange={e => updateField('industry', e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none">
                <option value="">Select...</option>
                {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">How long using BizBuilder? *</label>
              <select value={data.duration} onChange={e => updateField('duration', e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none">
                <option value="">Select...</option>
                {DURATIONS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Which tier? *</label>
              <select value={data.tier} onChange={e => updateField('tier', e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none">
                <option value="">Select...</option>
                {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Testimonial Type</label>
            <div className="flex gap-3">
              <button onClick={() => updateField('testimonialType', 'written')}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm transition-all ${
                  data.testimonialType === 'written'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}>
                <MessageSquare size={16} />
                Written Quote
              </button>
              <button onClick={() => updateField('testimonialType', 'video')}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm transition-all ${
                  data.testimonialType === 'video'
                    ? 'border-amber-500 bg-amber-50 text-amber-700'
                    : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}>
                <Video size={16} />
                Video
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Star Rating</label>
            <div className="flex gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <button key={i} onClick={() => updateField('starRating', i + 1)}>
                  <Star size={24} className={i < data.starRating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Testimonial Text (min 20, max 500 chars)</label>
            <textarea value={data.testimonialText} onChange={e => updateField('testimonialText', e.target.value)} rows={3} maxLength={500}
              placeholder="Share your experience..."
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            <p className="text-[10px] text-slate-400 mt-1">{data.testimonialText.length}/500</p>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Biggest Win So Far *</label>
            <textarea value={data.biggestWin} onChange={e => updateField('biggestWin', e.target.value)} rows={2}
              placeholder="What's the biggest result you've achieved?"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Revenue Growth %</label>
              <input type="number" min={0} value={data.revenueGrowth || ''} onChange={e => updateField('revenueGrowth', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
            <div>
              <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Customer Growth %</label>
              <input type="number" min={0} value={data.customerGrowth || ''} onChange={e => updateField('customerGrowth', parseInt(e.target.value) || 0)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Specific Results/Outcomes</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {RESULT_OPTIONS.map(ro => (
                <label key={ro.key} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                  data.results.includes(ro.key) ? 'border-amber-500 bg-amber-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
                }`}>
                  <input type="checkbox" checked={data.results.includes(ro.key)} onChange={() => toggleResult(ro.key)} className="sr-only" />
                  <CheckCircle size={12} className={data.results.includes(ro.key) ? 'text-amber-600' : 'text-slate-300'} />
                  <span className="text-xs text-slate-700">{ro.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">What Surprised You Most? *</label>
            <textarea value={data.whatSurprised} onChange={e => updateField('whatSurprised', e.target.value)} rows={2}
              placeholder="What was the biggest surprise about BizBuilder?"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">What Would You Tell Others? *</label>
            <textarea value={data.whatTellOthers} onChange={e => updateField('whatTellOthers', e.target.value)} rows={2}
              placeholder="What advice would you give another entrepreneur?"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-2">Photo Upload (optional)</label>
            <div className="border border-dashed border-slate-300 rounded-xl p-4 text-center">
              {selectedPhoto ? (
                <div className="space-y-2">
                  <img src={selectedPhoto} alt="Upload" className="w-20 h-20 object-cover rounded-lg mx-auto" />
                  <button onClick={() => setSelectedPhoto(null)}
                    className="text-[10px] text-red-500 font-black uppercase tracking-widest">Remove</button>
                </div>
              ) : (
                <button onClick={handlePhotoUpload} className="space-y-1">
                  <Upload size={20} className="mx-auto text-slate-400" />
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Upload Photo</p>
                  <p className="text-[8px] text-slate-400">Optional consent for social media use</p>
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Preferred Attribution Level</label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { key: 'full' as AttributionLevel, label: 'Full name + business' },
                { key: 'first_name' as AttributionLevel, label: 'First name only' },
                { key: 'anonymous' as AttributionLevel, label: 'Anonymous' },
              ]).map(opt => (
                <button key={opt.key} onClick={() => updateField('attributionLevel', opt.key)}
                  className={`px-3 py-2 rounded-xl border text-[10px] font-black uppercase tracking-widest transition-all ${
                    data.attributionLevel === opt.key
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100'
                  }`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Social Media Handle (optional)</label>
            <input type="text" value={data.socialHandle} onChange={e => updateField('socialHandle', e.target.value)}
              placeholder="@yourhandle"
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500/30 outline-none" />
          </div>

          <div className="p-4 border border-amber-200 bg-amber-50 rounded-xl">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={data.permissionToPublish} onChange={e => updateField('permissionToPublish', e.target.checked)}
                className="mt-0.5 rounded border-slate-300 text-amber-600 focus:ring-amber-500" />
              <div>
                <p className="text-xs font-bold text-amber-800">Permission to Publish *</p>
                <p className="text-[10px] text-amber-700 mt-1">{POPIA_NOTICE}</p>
              </div>
            </label>
          </div>

          <button onClick={handleSubmit} disabled={loading}
            className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
              loading ? 'bg-slate-300 text-slate-500 cursor-not-allowed' : 'bg-amber-600 text-white hover:bg-amber-700'
            }`}>
            <Send size={16} />
            {loading ? 'Submitting...' : 'Submit Testimonial'}
          </button>
        </div>
      )}
    </div>
  );
}
